import React from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { useTranslation } from 'react-i18next';
import { RoiServiceMetric } from '../types';

interface Props {
  services: RoiServiceMetric[];
}

const truncate = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

const CORNER_RADIUS = 8;
const SVG_NS = 'http://www.w3.org/2000/svg';

interface Corners {
  tl: boolean;
  tr: boolean;
  br: boolean;
  bl: boolean;
}

// Builds an SVG path for a rectangle with only the requested corners rounded.
const roundedRectPath = (x: number, y: number, width: number, height: number, radius: number, corners: Corners): string => {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  const tl = corners.tl ? r : 0;
  const tr = corners.tr ? r : 0;
  const br = corners.br ? r : 0;
  const bl = corners.bl ? r : 0;
  return [
    `M ${x + tl} ${y}`,
    `H ${x + width - tr}`,
    tr ? `A ${tr} ${tr} 0 0 1 ${x + width} ${y + tr}` : '',
    `V ${y + height - br}`,
    br ? `A ${br} ${br} 0 0 1 ${x + width - br} ${y + height}` : '',
    `H ${x + bl}`,
    bl ? `A ${bl} ${bl} 0 0 1 ${x} ${y + height - bl}` : '',
    `V ${y + tl}`,
    tl ? `A ${tl} ${tl} 0 0 1 ${x + tl} ${y}` : '',
    'Z',
  ]
    .filter(Boolean)
    .join(' ');
};

// Scoped to this chart instance only — runs after every (re)draw. Highcharts 9 has no
// per-corner borderRadius, so we replace each column's <rect> with a rounded <path>:
// hits (top of the stack) get the top corners, misses (bottom) get the bottom corners.
function roundColumnCorners(this: Highcharts.Chart): void {
  this.series.forEach((series, seriesIndex) => {
    const corners: Corners =
      seriesIndex === 0
        ? { tl: true, tr: true, br: false, bl: false } // hits — top of stack
        : { tl: false, tr: false, br: true, bl: true }; // misses — bottom of stack
    series.points.forEach(point => {
      const p = point as unknown as {
        shapeArgs?: { x?: number; y?: number; width?: number; height?: number };
        graphic?: { element: SVGElement };
      };
      const shape = p.shapeArgs;
      const graphic = p.graphic;
      if (!shape || !graphic?.element || shape.width == null || shape.height == null) {
        return;
      }
      const d = roundedRectPath(shape.x ?? 0, shape.y ?? 0, shape.width, shape.height, CORNER_RADIUS, corners);
      let el = graphic.element;
      if (el.tagName.toLowerCase() !== 'path') {
        const path = document.createElementNS(SVG_NS, 'path');
        Array.from(el.attributes).forEach(attr => {
          if (!['x', 'y', 'width', 'height', 'rx', 'ry'].includes(attr.name)) {
            path.setAttribute(attr.name, attr.value);
          }
        });
        el.parentNode?.replaceChild(path, el);
        graphic.element = path;
        el = path;
      }
      el.setAttribute('d', d);
    });
  });
}

const HitVsMissChart: React.FC<Props> = ({ services }) => {
  const { t } = useTranslation('admin');

  const options: Highcharts.Options = {
    chart: { type: 'column', height: 320, backgroundColor: 'transparent', events: { render: roundColumnCorners } },
    title: { text: '' },
    xAxis: {
      categories: services.map(s => truncate(s.serviceName, 18)),
      labels: { rotation: -45 },
    },
    yAxis: { title: { text: '' }, allowDecimals: false, gridLineColor: '#eee' },
    legend: { align: 'left', verticalAlign: 'top', layout: 'horizontal' },
    plotOptions: { column: { stacking: 'normal', borderWidth: 0 } },
    tooltip: { shared: true },
    series: [
      { type: 'column', name: t('roiInsights.chart.hits'), data: services.map(s => s.hits), color: '#6d28d9' },
      { type: 'column', name: t('roiInsights.chart.misses'), data: services.map(s => s.misses), color: '#e9d5ff' },
    ],
    credits: { enabled: false },
  };

  return (
    <div className="roi-panel hit-vs-miss-chart">
      <h3 className="roi-panel-title">{t('roiInsights.panels.hitVsMiss')}</h3>
      {services.length === 0 ? (
        <div className="roi-panel-empty">{t('roiInsights.noData')}</div>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </div>
  );
};

export default HitVsMissChart;
